import { ChangeEvent, ReactNode, useSyncExternalStore } from "react";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import type { ChipDropdownItem } from "../Chip";
import { Chip } from "../Chip";
import { IconButton } from "../IconButton";
import { Input } from "../Input";
import {
    ToolbarMobileActionsMenu,
    ToolbarMobileFiltersMenu,
} from "./TableToolbarNarrowGroups";
import styles from "./TableToolbar.module.scss";

const NARROW_MQ = "(max-width: 900px)";

function subscribeNarrow(onChange: () => void) {
    const mq = window.matchMedia(NARROW_MQ);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
}

function getNarrowSnapshot() {
    return window.matchMedia(NARROW_MQ).matches;
}

function getNarrowServerSnapshot() {
    return false;
}

type ToolbarMode = "title" | "filterable" | "active-filters" | "selection";

export interface TableToolbarFilter {
    id: string;
    label: string;
    count?: number;
    active?: boolean;
    onClick?: () => void;
    onRemove?: () => void;
    /** Opens a Radix menu from the chip (same pattern as `Chip` `items`). */
    menuItems?: ChipDropdownItem[];
}

export interface TableToolbarAction {
    id: string;
    label: string;
    onClick?: () => void;
    /** When set, renders a `Chip` dropdown instead of a plain text button. */
    menuItems?: ChipDropdownItem[];
}

export interface TableToolbarProps {
    className?: string;
    /**
     * Total for the current result set (e.g. filtered row count). Keep this
     * stable when users check rows; do not swap to selection count unless you
     * intentionally switch to `mode="selection"` for a bulk-action flow.
     */
    count: number;
    label: string;
    /**
     * `filterable` / `active-filters` / `title` keep the title as count + label.
     * Use `selection` only when you deliberately replace the title with bulk
     * actions (not for simple row checkbox selection).
     */
    mode?: ToolbarMode;
    filters?: TableToolbarFilter[];
    showMoreFilters?: boolean;
    onMoreFiltersClick?: () => void;
    onClearAllFilters?: () => void;
    selectionActions?: TableToolbarAction[];
    updatedAtLabel?: string;
    /** Utility icon buttons render only when their handler is provided. */
    onRefresh?: () => void;
    onDownload?: () => void;
    onColumns?: () => void;
    searchable?: boolean;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    /** Rendered after the search field (e.g. advanced filters menu trigger). */
    searchAccessory?: ReactNode;
    /** "More filters" chip opens this menu when provided. */
    moreFiltersMenuItems?: ChipDropdownItem[];
}

const ChevronDownIcon = () => <Icon icon="chevron-down" />;
const TuneIcon = () => <Icon icon="tune" />;
const RefreshIcon = () => <Icon icon="refresh" />;
const DownloadIcon = () => <Icon icon="download" />;
const ColumnsIcon = () => <Icon icon="columns" />;
const SearchIcon = () => <Icon icon="search" />;
const ClearSearchIcon = () => <Icon icon="close" />;

/**
 * Table toolbar (`TableToolbar 🚧 (Beta)`).
 * Supports title-only, filterable, active-filters and bulk-selection states.
 */
export function TableToolbar({
    className,
    count,
    label,
    mode = "title",
    filters = [],
    showMoreFilters = true,
    onMoreFiltersClick,
    onClearAllFilters,
    selectionActions = [],
    updatedAtLabel = "Updated 2min ago",
    onRefresh,
    onDownload,
    onColumns,
    searchable = true,
    searchValue = "",
    onSearchChange,
    searchAccessory,
    moreFiltersMenuItems,
}: TableToolbarProps) {
    const showFilterRows = mode === "filterable" || mode === "active-filters";
    const showSelection = mode === "selection";
    const hasActiveFilters = filters.some(f => f.active);
    const showFilterableClear =
        mode === "filterable" && hasActiveFilters && Boolean(onClearAllFilters);

    const showSubLeading = showSelection || showFilterRows;
    const showSearch = searchable;
    const mobileSingleRow = !showSubLeading && !showSearch;
    const narrow = useSyncExternalStore(
        subscribeNarrow,
        getNarrowSnapshot,
        getNarrowServerSnapshot,
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        onSearchChange?.(event.target.value);
    };

    const handleClearSearch = () => {
        onSearchChange?.("");
    };

    const filtersBlock = showFilterRows ? (
        <div className={styles.filters}>
            {filters.map(filter => {
                const hasMenu =
                    Boolean(filter.menuItems?.length) && !filter.onRemove;
                return (
                    <Chip
                        counter={filter.active ? filter.count : undefined}
                        iconRight={
                            filter.active ? undefined : hasMenu ? undefined : (
                                <ChevronDownIcon />
                            )
                        }
                        items={hasMenu ? filter.menuItems : undefined}
                        key={filter.id}
                        label={filter.label}
                        onClick={hasMenu ? undefined : filter.onClick}
                        onRemove={filter.active ? filter.onRemove : undefined}
                        selected={filter.active}
                    />
                );
            })}
            {showMoreFilters ? (
                <Chip
                    iconRight={<TuneIcon />}
                    items={
                        moreFiltersMenuItems?.length
                            ? moreFiltersMenuItems
                            : undefined
                    }
                    label="More Filters"
                    onClick={
                        moreFiltersMenuItems?.length
                            ? undefined
                            : onMoreFiltersClick
                    }
                />
            ) : null}
            {mode === "active-filters" && onClearAllFilters ? (
                <button
                    className={styles.bulkAction}
                    onClick={onClearAllFilters}
                    type="button"
                >
                    Clear all
                </button>
            ) : null}
            {showFilterableClear ? (
                <button
                    className={styles.bulkAction}
                    onClick={onClearAllFilters}
                    type="button"
                >
                    Clear
                </button>
            ) : null}
        </div>
    ) : null;

    const selectionBlock = showSelection ? (
        <div className={styles.bulk}>
            {selectionActions.map(action =>
                action.menuItems?.length ? (
                    <Chip
                        color="default"
                        items={action.menuItems}
                        key={action.id}
                        label={action.label}
                    />
                ) : (
                    <button
                        className={styles.bulkAction}
                        key={action.id}
                        onClick={action.onClick}
                        type="button"
                    >
                        {action.label}
                    </button>
                ),
            )}
        </div>
    ) : null;

    return (
        <div
            className={cx(styles.root, className)}
            data-mobile-leading-only={
                showSubLeading && !showSearch ? true : undefined
            }
            data-mobile-search-only={
                !showSubLeading && showSearch ? true : undefined
            }
            data-mobile-single={mobileSingleRow ? true : undefined}
            data-ui="table-toolbar"
        >
            <div className={styles.title}>
                <span>{count}</span>
                <span>{label}</span>
            </div>
            {showSubLeading ? (
                <div className={styles.subLeading}>
                    {showSelection ? (
                        narrow ? (
                            <ToolbarMobileActionsMenu
                                actions={selectionActions}
                            />
                        ) : (
                            selectionBlock
                        )
                    ) : null}
                    {showFilterRows ? (
                        narrow ? (
                            <ToolbarMobileFiltersMenu
                                filters={filters}
                                mode={mode}
                                moreFiltersMenuItems={moreFiltersMenuItems}
                                onClearAllFilters={onClearAllFilters}
                                onMoreFiltersClick={onMoreFiltersClick}
                                showFilterableClear={showFilterableClear}
                                showMoreFilters={showMoreFilters}
                            />
                        ) : (
                            filtersBlock
                        )
                    ) : null}
                </div>
            ) : null}
            <div className={styles.utilities}>
                {updatedAtLabel ? (
                    <span className={styles.updated}>{updatedAtLabel}</span>
                ) : null}
                {onRefresh ? (
                    <IconButton
                        aria-label="Refresh data"
                        onClick={onRefresh}
                        size="md"
                        variant="tertiary"
                    >
                        <RefreshIcon />
                    </IconButton>
                ) : null}
                {onDownload ? (
                    <IconButton
                        aria-label="Download table"
                        onClick={onDownload}
                        size="md"
                        variant="tertiary"
                    >
                        <DownloadIcon />
                    </IconButton>
                ) : null}
                {onColumns ? (
                    <IconButton
                        aria-label="Table columns"
                        onClick={onColumns}
                        size="md"
                        variant="tertiary"
                    >
                        <ColumnsIcon />
                    </IconButton>
                ) : null}
            </div>
            {showSearch ? (
                <div className={styles.searchRow}>
                    <Input
                        className={cx(styles.search, styles.searchToolbar)}
                        iconLeft={<SearchIcon />}
                        iconRight={
                            searchValue ? (
                                <button
                                    aria-label="Clear search"
                                    className={styles.searchClear}
                                    onClick={handleClearSearch}
                                    type="button"
                                >
                                    <ClearSearchIcon />
                                </button>
                            ) : null
                        }
                        label="Search"
                        labelBehavior="inline"
                        onChange={handleSearch}
                        value={searchValue}
                    />
                    {searchAccessory}
                </div>
            ) : null}
        </div>
    );
}

TableToolbar.displayName = "TableToolbar";
